// Inline-expand modifier selection (design contract:
// docs/mockups/nested-addons/cashier-nested-addons.html, layout A).
//
// Everything sits on one scrolling sheet. Selecting an option that carries its
// own groups expands them in place, directly beneath it. Groups whose options
// can nest render as ROWS rather than chips — a child panel cannot open under
// a wrapped chip without the layout jumping.
//
// Tablet/desktop keeps the centred `Modal`. Phones get the F15 bottom-sheet
// pattern instead (grab handle, scrim, role="dialog") — see
// SellSurface.js:1522-1570 for the sheet this was copied from. Body and
// footer are built once as local variables and handed to whichever chrome
// renders them, so the two branches cannot drift apart.
import { createPortal } from 'react-dom';
import React from 'react';
import Modal from '../../common/Modal';
import { useLanguage } from '../../../context/LanguageContext';
import { nodeAt, groupNests, ruleLabel } from '../../../hooks/pos/lib/modifierTree';
import useIsMobile from '../../../hooks/useIsMobile';

const CSS = `
.msel-miss{display:flex;align-items:center;gap:8px;margin-bottom:12px;padding:8px 12px;
  border-radius:10px;font-size:12px;font-weight:700;
  color:var(--c-red);background:var(--c-redLight);border:1px solid var(--c-redBorder);}
.msel-group{margin-bottom:16px;}
.msel-group.unmet{background:var(--c-redLight);border:1px solid var(--c-redBorder);
  border-radius:12px;padding:10px 12px;margin-inline:-12px;}
.msel-ghead{display:flex;align-items:center;gap:8px;margin-bottom:8px;flex-wrap:wrap;}
.msel-gname{font-size:13.5px;font-weight:800;color:var(--c-ink);}
.msel-grule{font-size:9.5px;font-weight:800;letter-spacing:.6px;text-transform:uppercase;
  color:var(--c-muted);background:var(--c-faint);border:1px solid var(--c-border);
  border-radius:6px;padding:2px 7px;}
.msel-grule.req{color:var(--c-red);background:var(--c-redLight);border-color:var(--c-redBorder);}
.msel-grule.ok{color:var(--c-green);background:var(--c-greenLight);border-color:var(--c-greenBorder);}
.msel-chips{display:flex;flex-wrap:wrap;gap:8px;}
.msel-chip{display:inline-flex;align-items:center;gap:7px;min-height:44px;padding:0 15px;
  border-radius:12px;border:1.5px solid var(--c-border);background:var(--c-surface);
  font-family:inherit;font-size:13.5px;font-weight:700;color:var(--c-ink);cursor:pointer;}
.msel-chip.on{border-color:var(--c-green);background:var(--c-greenLight);color:var(--c-green);}
.msel-rows{display:flex;flex-direction:column;gap:7px;}
.msel-row{display:flex;align-items:center;gap:11px;width:100%;min-height:50px;padding:0 14px;
  border-radius:12px;border:1.5px solid var(--c-border);background:var(--c-surface);
  font-family:inherit;font-size:13.5px;font-weight:700;color:var(--c-ink);
  text-align:start;cursor:pointer;}
.msel-row.on{border-color:var(--c-green);background:var(--c-greenLight);}
.msel-row .nm{flex:1;min-width:0;}
.msel-price{font-size:11.5px;font-weight:700;color:var(--c-muted);}
.msel-nest{margin:9px 0 3px 24px;padding:11px 13px;border-inline-start:3px solid var(--c-green);
  background:var(--c-faint);border-radius:0 12px 12px 0;}
.msel-nestfor{font-size:10px;font-weight:800;letter-spacing:.6px;text-transform:uppercase;
  color:var(--c-green);margin-bottom:8px;}
.msel-foot{display:flex;align-items:center;gap:12px;width:100%;}
.msel-tot{flex:1;}
.msel-tot .l{font-size:10px;font-weight:800;letter-spacing:.7px;text-transform:uppercase;color:var(--c-muted);}
.msel-tot .v{font-size:18px;font-weight:800;color:var(--c-ink);}

/* Phone sheet — same geometry as SellSurface's .slf-scrim/.slf-sheet/.slf-grab
   (SellSurface.js:1522-1570). Kept local rather than shared because this
   modal has its own stacking context (a Modal-less overlay), not the pos-app
   surface's z-index ladder. */
.msel-scrim{position:fixed;inset:0;z-index:50;
  background:color-mix(in srgb, var(--c-ink) 42%, transparent);
  animation:msel-scrim-in .2s ease;}
.msel-sheet{position:fixed;left:0;right:0;bottom:0;z-index:51;
  max-height:88dvh;display:flex;flex-direction:column;overflow:hidden;
  background:var(--c-surface);border-radius:22px 22px 0 0;
  border-top:1px solid color-mix(in srgb, var(--c-border) 55%, transparent);
  box-shadow:0 -18px 50px color-mix(in srgb, var(--c-ink) 22%, transparent);
  animation:msel-sheet-in .28s cubic-bezier(.32,.72,0,1);}
@keyframes msel-scrim-in{from{opacity:0;}to{opacity:1;}}
@keyframes msel-sheet-in{from{transform:translateY(100%);}to{transform:translateY(0);}}
@media (prefers-reduced-motion: reduce){
  .msel-scrim,.msel-sheet{animation:none;}
}
.msel-grab{flex:none;width:100%;min-height:44px;display:flex;align-items:center;
  justify-content:center;cursor:pointer;}
.msel-grab i{display:block;width:38px;height:4px;border-radius:999px;
  background:color-mix(in srgb, var(--c-border) 90%, transparent);}
.msel-sheet-title{flex:none;padding:0 18px 10px;font-size:16px;font-weight:800;color:var(--c-ink);}
.msel-sheet-body{flex:1;min-height:0;overflow-y:auto;padding:0 18px 12px;}
.msel-sheet-foot{flex:none;padding:11px 18px 14px;border-top:1px solid var(--c-border);
  background:var(--c-surface);}
`;

const money = (currency, value) => `${currency}${(parseFloat(value) || 0).toFixed(2)}`;

const AddonSelectionModal = ({
    isOpen, onClose, currentProduct, groupIds, catalog, tree,
    onToggle, unmet, flagUnmet, lineTotal, onAdd, currency,
}) => {
    const { t } = useLanguage();
    const isMobile = useIsMobile();

    if (!currentProduct) return null;

    const isRequired = (group) => (group.min_select || 0) > 0;

    const countIn = (node, group) => Object.keys(node[group.group_id] || {}).length;

    const groupSatisfied = (node, group) => {
        const n = countIn(node, group);
        const max = group.max_select;
        return n >= (group.min_select || 0) && (max == null || n <= max);
    };

    const renderOption = (node, path, group, code) => {
        const option = catalog.options[code];
        if (!option) return null;
        const selected = code in (node[group.group_id] || {});
        const nests = (option.groups || []).length > 0;
        const label = option.price
            ? `${option.name} +${money(currency, option.price)}`
            : option.name;

        if (!groupNests(group, catalog)) {
            return (
                <button
                    key={code}
                    type="button"
                    className={`msel-chip${selected ? ' on' : ''}`}
                    aria-pressed={selected}
                    aria-label={label}
                    onClick={() => onToggle(path, group.group_id, code)}
                >
                    <span>{option.name}</span>
                    {option.price ? (
                        <span className="msel-price">+{money(currency, option.price)}</span>
                    ) : null}
                </button>
            );
        }

        const childPath = [...path, [group.group_id, code]];
        return (
            <React.Fragment key={code}>
                <button
                    type="button"
                    className={`msel-row${selected ? ' on' : ''}`}
                    aria-pressed={selected}
                    aria-label={label}
                    onClick={() => onToggle(path, group.group_id, code)}
                >
                    <span className="nm">{option.name}</span>
                    {option.price ? (
                        <span className="msel-price">+{money(currency, option.price)}</span>
                    ) : null}
                </button>
                {selected && nests && (
                    <div className="msel-nest">
                        <div className="msel-nestfor">
                            {t('because_option_selected', { option: option.name })}
                        </div>
                        {option.groups.map(childId =>
                            renderGroup(nodeAt(tree, childPath), childPath, childId))}
                    </div>
                )}
            </React.Fragment>
        );
    };

    const renderGroup = (node, path, groupId) => {
        const group = catalog.groups[groupId];
        if (!group) return null;
        const satisfied = groupSatisfied(node, group);
        const showUnmet = flagUnmet && !satisfied;
        const nests = groupNests(group, catalog);
        const ruleKey = ruleLabel(group);

        return (
            <div className={`msel-group${showUnmet ? ' unmet' : ''}`} key={`${path.length}-${groupId}`}>
                <div className="msel-ghead">
                    <span className="msel-gname">{group.name}</span>
                    <span className={`msel-grule${isRequired(group)
                        ? (satisfied ? ' ok' : ' req') : ''}`}>
                        {t(ruleKey, { max: group.max_select, min: group.min_select })}
                    </span>
                </div>
                <div className={nests ? 'msel-rows' : 'msel-chips'}>
                    {(group.options || []).map(code => renderOption(node, path, group, code))}
                </div>
            </div>
        );
    };

    const body = (
        <>
            <style>{CSS}</style>
            {flagUnmet && unmet.length > 0 && (
                <div className="msel-miss" role="alert">
                    {t('still_needed')}: {unmet.join(' · ')}
                </div>
            )}
            {(groupIds || []).map(groupId => renderGroup(tree, [], groupId))}
            {(groupIds || []).length === 0 && (
                <p className="text-sm text-gray-500">{t('no_addons_available')}</p>
            )}
        </>
    );

    const footer = (
        <div className="msel-foot">
            <div className="msel-tot">
                <div className="l">{t('line_total')}</div>
                <div className="v money">{money(currency, lineTotal)}</div>
            </div>
            <button
                type="button"
                className="inline-flex justify-center items-center min-h-11 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md"
                onClick={onClose}
            >
                {t('cancel')}
            </button>
            <button
                type="button"
                className="inline-flex justify-center items-center min-h-11 px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md disabled:opacity-40"
                disabled={unmet.length > 0}
                onClick={onAdd}
            >
                {t('add_to_order')}
            </button>
        </div>
    );

    if (!isOpen) return null;

    if (isMobile) {
        // Portaled for the same reason the tablet branch's <Modal/> is: this
        // sheet is `position:fixed`, and it opens from CatalogBrowse inside
        // the station's glass pane. A backdrop-filter ancestor would become
        // its containing block and the sheet would size itself to that pane
        // instead of the viewport. See pos-app/fixedOverlayPortals.test.js.
        return createPortal(
            <>
                <style>{CSS}</style>
                <div
                    className="msel-scrim"
                    data-testid="modifier-scrim"
                    onClick={onClose}
                />
                <div
                    className="msel-sheet"
                    data-testid="modifier-sheet"
                    role="dialog"
                    aria-modal="true"
                    aria-label={currentProduct.name}
                >
                    <button
                        type="button"
                        className="msel-grab"
                        aria-label={t('close')}
                        onClick={onClose}
                    >
                        <i />
                    </button>
                    <div className="msel-sheet-title">{currentProduct.name}</div>
                    <div className="msel-sheet-body">
                        {body}
                    </div>
                    <div className="msel-sheet-foot" data-testid="modifier-foot">
                        {footer}
                    </div>
                </div>
            </>,
            document.body
        );
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={currentProduct.name}
            footer={footer}
        >
            {body}
        </Modal>
    );
};

export default AddonSelectionModal;
